import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import EnhancedSearch from '../components/EnhancedSearch'
import SearchResultsGrid from '../components/SearchResultsGrid'

interface SearchResult {
  product: {
    id: string
    name: string
    description: string
    extractType: string
    type: string
    sizes: Array<{ weight: string; price: number; inStock: boolean }>
    images: string[]
    inStock: boolean
    featured: boolean
    categories: string[]
    tags: string[]
    dateAdded: Date
    salesCount: number
    rating: number
    reviewCount: number
    potency?: string
    effects?: string[]
  }
  vendor: {
    id: string
    name: string
    verified: boolean
    subscriptionPlan?: string
    rating: number
  }
  score: number
  isFeatured: boolean
  isPremiumVendor: boolean
}

export default function EnhancedSearchPage() {
  const location = useLocation()
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [initialQuery, setInitialQuery] = useState('')

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const query = searchParams.get('q')
    if (query) {
      setInitialQuery(query)
    }
  }, [location.search])

  const handleSearchResults = (results: SearchResult[]) => {
    setSearchResults(results)
  }

  return (
    <div className="enhanced-search-page">
      <div className="search-page-header">
        <h1>Find Your Perfect Kratom Products</h1>
        <p>Search through our extensive catalog of 7OH, MIT, and traditional kratom products with advanced filtering.</p>
      </div>

      <div className="search-container">
        <EnhancedSearch 
          onResults={handleSearchResults}
          showFilters={true}
          initialQuery={initialQuery}
        />
      </div>

      <div className="results-container">
        <SearchResultsGrid 
          results={searchResults}
          loading={isLoading}
        />
      </div>

      {/* Premium Placement Info */}
      <div className="premium-info">
        <div className="premium-info-content">
          <h3>🏪 Are you a vendor?</h3>
          <p>Get your products featured at the top of search results with our premium vendor subscriptions.</p>
          <div className="premium-benefits">
            <div className="benefit">
              <span className="benefit-icon">⭐</span>
              <span>Featured product placement</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">🔝</span>
              <span>Priority in search results</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">📊</span>
              <span>Detailed analytics</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">💳</span>
              <span>Premium vendor badge</span>
            </div>
          </div>
          <button className="vendor-signup-btn">
            Learn About Vendor Plans
          </button>
        </div>
      </div>

      <style>{`
        .enhanced-search-page {
          min-height: 100vh;
          background: #1a202c;
          padding: 20px;
        }

        .search-page-header {
          max-width: 1200px;
          margin: 0 auto 30px auto;
          text-align: center;
          padding: 40px 20px;
          background: linear-gradient(135deg, #2c5530 0%, #4caf50 100%);
          color: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(44, 85, 48, 0.3);
        }

        .search-page-header h1 {
          font-size: 2.5em;
          margin-bottom: 15px;
          font-weight: 700;
        }

        .search-page-header p {
          font-size: 1.2em;
          opacity: 0.9;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.5;
        }

        .search-container {
          max-width: 1200px;
          margin: 0 auto 30px auto;
        }

        .results-container {
          max-width: 1200px;
          margin: 0 auto 60px auto;
        }

        .premium-info {
          max-width: 800px;
          margin: 60px auto 0 auto;
          background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
          border-radius: 16px;
          border: 2px solid #ffa726;
          overflow: hidden;
        }

        .premium-info-content {
          padding: 40px;
          text-align: center;
        }

        .premium-info h3 {
          color: #e65100;
          font-size: 1.8em;
          margin-bottom: 15px;
        }

        .premium-info p {
          color: #bf360c;
          font-size: 1.1em;
          margin-bottom: 30px;
          line-height: 1.6;
        }

        .premium-benefits {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .benefit {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: #2d3748;
          padding: 15px;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          font-weight: 500;
          color: #e2e8f0;
        }

        .benefit-icon {
          font-size: 1.5em;
        }

        .vendor-signup-btn {
          background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
          color: white;
          border: none;
          padding: 15px 30px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(255, 152, 0, 0.3);
        }

        .vendor-signup-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(255, 152, 0, 0.4);
        }

        @media (max-width: 768px) {
          .enhanced-search-page {
            padding: 15px;
          }

          .search-page-header {
            padding: 30px 20px;
            margin-bottom: 20px;
          }

          .search-page-header h1 {
            font-size: 2em;
          }

          .search-page-header p {
            font-size: 1em;
          }

          .premium-benefits {
            grid-template-columns: 1fr;
            gap: 15px;
          }

          .premium-info-content {
            padding: 30px 20px;
          }
        }
      `}</style>
    </div>
  )
}